// Mô hình soạn mẫu tin ZBS ở giao diện. Quy tắc kiểm tra nằm ở backend (POST /zns/templates/validate);
// ở đây chỉ có kiểu dữ liệu và các hàm dựng form, để giao diện không chép lại luật.

export type TemplateType = 1 | 2 | 3 | 4 | 5
export type TemplateTag = 1 | 2 | 3

export interface DesignParam { name: string; type: string; sampleValue: string }
export interface DesignButton { type: number; title: string; content: string }
export interface DesignTableRow { title: string; value: string; rowType?: number }
export interface DesignRatingItem { rate: number; title: string; question?: string; answers?: string[]; thanks: string; description: string }

export interface ZnsTemplateDesign {
  templateName: string
  templateType: TemplateType
  tag: TemplateTag
  header: { logo?: { light: string; dark: string }; images?: string[] }
  title?: string
  paragraphs?: string[]
  table?: DesignTableRow[]
  otp?: string
  voucher?: { name: string; condition: string; voucherCode: string; startDate?: string; endDate: string }
  payment?: { bankCode: string; accountName: string; bankAccount: string; amount: string; note?: string }
  rating?: { option: string; items: DesignRatingItem[] }
  buttons?: DesignButton[]
  params: DesignParam[]
  note?: string
}

export interface TemplateSpec {
  templateTypes: Array<{
    value: TemplateType
    label: string
    tags: TemplateTag[]
    allowImages: boolean
    allowTable: boolean
    maxParagraphs: number
    minButtons: number
    maxButtons: number
  }>
  tags: Array<{ value: TemplateTag; label: string }>
  paramTypes: Array<{ value: string; label: string; maxLength: number }>
  buttonTypes: Array<{ value: number; label: string; kind: 'url' | 'phone' | 'text' }>
  rowTypes: Array<{ value: number; label: string }>
  ratingOptions: Array<{ value: string; count: number }>
  limits: { nameMin: number; nameMax: number; imageMaxBytes: number; dailySubmitQuota: number }
}

const PARAM_RE = /<([^<>\s]+)>/g

/** Tên tham số `<ten>` xuất hiện trong chuỗi, theo thứ tự, không trùng. */
export function paramsIn(text: string | undefined): string[] {
  if (!text) return []
  return Array.from(new Set(Array.from(text.matchAll(PARAM_RE), (m) => m[1])))
}

/** Mọi chuỗi trong mẫu có thể chứa tham số. */
export function designTexts(d: ZnsTemplateDesign): string[] {
  const out: string[] = []
  if (d.title) out.push(d.title)
  out.push(...(d.paragraphs ?? []))
  for (const r of d.table ?? []) out.push(r.value)
  if (d.otp) out.push(d.otp)
  if (d.voucher) out.push(d.voucher.name, d.voucher.condition, d.voucher.voucherCode, d.voucher.startDate ?? '', d.voucher.endDate)
  if (d.payment) out.push(d.payment.amount, d.payment.note ?? '')
  for (const b of d.buttons ?? []) out.push(b.content)
  return out
}

/** Loại tham số gợi ý theo vị trí dùng (OTP → 12, số tiền → 14, mã voucher → 4 …). */
function suggestedType(name: string, d: ZnsTemplateDesign): string {
  if (d.otp && paramsIn(d.otp).includes(name)) return '12'
  if (d.payment && paramsIn(d.payment.amount).includes(name)) return '14'
  if (d.payment && paramsIn(d.payment.note).includes(name)) return '15'
  if (d.voucher && paramsIn(d.voucher.voucherCode).includes(name)) return '4'
  if (d.voucher && [...paramsIn(d.voucher.startDate), ...paramsIn(d.voucher.endDate)].includes(name)) return '11'
  if ((d.buttons ?? []).some((b) => paramsIn(b.content).includes(name))) return '13'
  return '5'
}

/**
 * Đồng bộ danh sách tham số khai báo với những gì xuất hiện trong văn bản:
 * giữ loại + giá trị mẫu đã nhập, thêm tham số mới (loại gợi ý), bỏ tham số không còn dùng.
 */
export function syncParams(d: ZnsTemplateDesign): DesignParam[] {
  const existing = new Map(d.params.map((p) => [p.name, p]))
  const used: string[] = []
  for (const t of designTexts(d)) for (const n of paramsIn(t)) if (!used.includes(n)) used.push(n)
  return used.map((n) => existing.get(n) ?? { name: n, type: suggestedType(n, d), sampleValue: '' })
}

export function blankDesign(templateType: TemplateType = 1): ZnsTemplateDesign {
  const base: ZnsTemplateDesign = {
    templateName: '',
    templateType,
    tag: templateType === 5 ? 2 : 1,
    header: {},
    params: [],
    note: '',
  }
  // Nội dung khởi tạo (đoạn OTP, tên nút) là DỮ LIỆU MẪU gửi Zalo duyệt — người soạn sẽ sửa lại trước khi gửi
  // thật; giữ tiếng Việt bất kể ngôn ngữ giao diện người soạn đang chọn (không phải chuỗi UI).
  switch (templateType) {
    case 2:
      return { ...base, otp: '<otp>', paragraphs: ['Tuyệt đối KHÔNG chia sẻ mã xác thực cho bất kỳ ai dưới bất kỳ hình thức nào. Mã xác thực có hiệu lực trong 5 phút.'] } // i18n-ignore
    case 3:
      return { ...base, title: '', paragraphs: [], payment: { bankCode: '', accountName: '', bankAccount: '', amount: '', note: '' }, buttons: [{ type: 11, title: 'Thanh toán ngay', content: '' }] } // i18n-ignore
    case 4:
      return { ...base, title: '', paragraphs: [], voucher: { name: '', condition: '', voucherCode: '', endDate: '' }, buttons: [{ type: 12, title: 'Xem chi tiết', content: '' }] } // i18n-ignore
    case 5:
      return {
        ...base,
        title: '',
        paragraphs: [],
        rating: { option: 'five_stars', items: ratingItems(5) },
        buttons: [],
      }
    default:
      return { ...base, title: '', paragraphs: [''], table: [], buttons: [] }
  }
}

export function ratingItems(count: number, prev: DesignRatingItem[] = []): DesignRatingItem[] {
  return Array.from({ length: count }, (_, i) => prev[i] ?? { rate: i + 1, title: '', thanks: '', description: '' })
}

/** Chuẩn hoá trước khi gửi: bỏ chuỗi rỗng ở các danh sách tuỳ chọn và cắt khoảng trắng đầu / cuối. */
export function cleanDesign(d: ZnsTemplateDesign): ZnsTemplateDesign {
  const t = (s?: string) => (s ?? '').trim()
  return {
    ...d,
    templateName: t(d.templateName),
    title: d.title !== undefined ? t(d.title) : undefined,
    paragraphs: d.paragraphs?.map(t).filter(Boolean),
    table: d.table?.map((r) => ({ ...r, title: t(r.title), value: t(r.value) })).filter((r) => r.title || r.value),
    buttons: d.buttons?.map((b) => ({ ...b, title: t(b.title), content: t(b.content) })).filter((b) => b.title || b.content),
    params: syncParams(d),
    note: t(d.note) || undefined,
  }
}
